import React, { useState, useEffect, useMemo } from 'react';
import TranslatableDataGrid from '../shared/TranslatableDataGrid';
import StorefrontAdminTheme from '../shared/MUIStyles/StorefrontAdminTheme';
import { Typography, CircularProgress, ThemeProvider, Paper, Box, Button, TextField, IconButton, DialogActions, InputLabel } from '@mui/material';
import { useStorefrontAdministrationConfig } from '../../hooks/StorefrontAdministrationConfigContext';
import { useStorefrontSettings } from '../../hooks/StorefrontSettingsContext';
import Storefront from '../../services/Storefront';
import Loading from '../Loading';
import { GridToolbarContainer, GridToolbarFilterButton, GridToolbarExport, GridToolbarDensitySelector } from '@mui/x-data-grid-premium';
import MUIModal from '../shared/MUIModal';
import MUIAlertModal from '../shared/MUIAlertModal';
import DialogContentText from '@mui/material/DialogContentText';
import { Row, Col } from "react-bootstrap";
import Api from '../../services/Api';
import { CSSTransition } from "react-transition-group";
import Notifications from '../../services/Notifications';
import DateHelper from "../../helpers/DateHelper";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, parse, isValid, parseISO } from "date-fns";

const ItemRestrictionAdministration = () => {
    const theme = StorefrontAdminTheme;
    const localized = useStorefrontAdministrationConfig().labels;
    const storeData = useStorefrontSettings();
    const dateFormat = "yyyy-MM-dd";

    const [rows, setRows] = useState([]);
    const [fetching, setFetching] = useState(false);
    const [openAdd, setOpenAdd] = useState(false);
    const [sku, setSku] = useState("");
    const [description, setDescription] = useState('');
    const [itemList, setItemList] = useState([]);
    const [addItemList, setAddItemList] = useState([]);
    const [isVisible, setIsVisible] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [openUpdate, setOpenUpdate] = useState(false);
    const [salesDate, setSalesDate] = useState(null);
    const [itemFetching, setItemFetching] = useState(false);
    const [submitFlag, setSubmitFlag] = useState(true);
    const [itemerror, setItemerror] = useState(false);
    const [dateError, setDateError] = useState(false);
    const [itemValid, setItemValid] = useState(false);
    const [addFlage, setAddFlage] = useState(true);


    useEffect(() => {
        const loadRestrictedItems = async () => {
            await getRestrictedItems();
        };
        loadRestrictedItems();
    }, []);

    const getRestrictedItems = async () => {
        setFetching(true);
        var rows = [];
        await Storefront.getRestrictedItems(storeData.storefrontNumber).then((data) => {
            if (data) {
                data.forEach((fav) => {
                    rows.push({
                        id: fav.productId,
                        sku: fav.itemNumber,
                        description: fav.productDescription,
                        effectiveDateForSale: formatToYyyyDdMm(parseISO(fav.effectiveDateForSale)),
                    });
                });
                setRows(rows);
            }
            else {
                setRows([]);
            }
            setFetching(false);
        });
    }

    const columns = [
        {
            field: 'sku',
            headerName: localized.FavoriteItem,
            headerAlign: 'left',
            flex: 1
        },
        {
            field: 'description',
            headerName: localized.ItemDescription,
            headerAlign: 'left',
            flex: 1
        }, {
            field: 'effectiveDateForSale',
            headerName: localized.EffectiveSaleDate,
            headerAlign: 'left',
            flex: 1
        },
        {
            field: 'updateButton',
            headerName: localized.StorefrontAdminUpdate,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => {
                return (
                    <strong>
                        <Button
                            variant="standard"
                            color="update"
                            style={{ marginLeft: 16 }}
                            onClick={(e) => handleClickOpenUpdate(e, params.row)}
                        >
                            {localized.StorefrontAdminUpdate}
                        </Button>
                    </strong>
                )
            },
            disableClickEventBubbling: true

        },
        {
            field: 'deleteButton',
            headerName: localized.StorefrontAdminDelete,
            headerAlign: 'center',
            align: 'center',
            headerClassName: 'columnTopper',
            renderCell: (params) => {
                return (
                    <strong>
                        <Button
                            variant="standard"
                            color="error"
                            style={{ marginLeft: 16 }}
                            onClick={(e) => handleClickOpenDelete(e, params.row)}
                        >
                            {localized.StorefrontAdminDelete}
                        </Button>
                    </strong>
                )
            },
            disableClickEventBubbling: true
        }
    ];

    const renderAddButton = () => (
        <strong>
            <Button
                variant="contained"
                color="success"
                style={{ marginLeft: 16 }}
                onClick={handleClickOpenAdd}
            >
                {localized.StorefrontAdminAdd}
            </Button>
        </strong>
    );

    const customToolbar = () => {
        return (
            <GridToolbarContainer>
                <Box sx={{ flexGrow: 1 }} />
                <GridToolbarFilterButton
                    slotProps={{
                        tooltip: { title: 'Filter list' }
                    }}
                    variant="outlined"
                    color="update"
                />
                <GridToolbarDensitySelector
                    slotProps={{
                        tooltip: { title: 'Change density' }
                    }}
                    variant="outlined"
                    color="update"
                />

                <GridToolbarExport
                    printOptions={{ disableToolbarButton: true }}
                    slotProps={{
                        tooltip: { title: 'Export data' }
                    }}
                    variant="outlined"
                    color="update"
                />
                {renderAddButton()}
            </GridToolbarContainer>
        );
    }
    const loadAvailableSKUs = async (data) => {
        if (data) {
            await setItemList(data);
            setItemFetching(false);
            setItemValid(true);
            setItemerror(false);
            setDescription(data.description);
            if (salesDate) {
                setAddFlage(false);
            }
        }
        else {
            setItemFetching(false);
            setItemValid(false);
            setItemerror(true);
            setAddFlage(true);
            Notifications.error(localized.ItemNotFound);
        }

    }
    const fetchProductItem = async () => {
        setItemFetching(true);
        const storefrontId = storeData.storefront.id
        let url = `/api/products/ProductItem/${storefrontId}/${sku}`;
        await Api.fetch(url).then(loadAvailableSKUs);
    }

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData("text")?.trim() || "";
        setSku(pastedText);
        e.target.value = pastedText;
    };
    const handleChange = (event) => {
        setSku(event.currentTarget.value);
        setDescription("");
        setAddFlage(true);
        if (event.currentTarget.value != "") {
            setItemerror(false);
        }
    };
    const handleKeyDown = async (e) => {
        if (e.key === "Tab" || e.key == 'Enter') {
            if (sku != "") {
                if (!rows.some(x => x.sku === sku) && !addItemList.some(item => item.sku === sku))
                    await fetchProductItem();
                else {
                    setItemerror(true);
                    Notifications.error(localized.RestrictedItemExist);
                }
            }

        }
    }
    const handleBlur = async () => {
        if (sku == '') {
            setItemerror(true);
        }
        else {
            if (description == '') {
                if (!addItemList.some(item => item.sku === sku) && (rows.filter(x => x.sku == sku).length == 0 || rows.length == 0)) {
                    await fetchProductItem();
                } else {
                    setItemerror(true);
                    Notifications.error(localized.RestrictedItemExist);
                }
            }
        }
    }
    const handleClickOpenDelete = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setOpenDelete(true);
    };
    const handleCloseDelete = () => {
        setOpenDelete(false);
    };
    const handleClickOpenUpdate = (e, row) => {
        e.stopPropagation();
        setSalesDate(row.effectiveDateForSale);
        setSelectedRow(row);
        setOpenUpdate(true);
    };

    const handleCloseUpdate = () => {
        setSalesDate(null);
        setOpenUpdate(false);
    };
    const updateItemRestriction = async (form, type) => {
        if (type == 'add') {
            submitItemRestriction(type);
        }
        else {
            if (type == 'delete' || (type == 'update' && form.EffectiveDateForSale !== undefined)) {
                let request = [];
                var item = {
                    productId: form.Id ?? null,
                    itemNumber: form.Sku ?? null,
                    storefrontNumber: storeData.storefrontNumber,
                    effectiveDateForSale: form.EffectiveDateForSale ?? null,
                    operationType: type
                };
                request.push(item);
                return await Storefront.updateRestrictedItems(request).then((res) => {
                    if (res) {
                        const notificationType = res.response.endsWith("Success") ? "message" : "error";

                        if (notificationType == 'message') {
                            if (type == "delete") {
                                setSalesDate(undefined);
                                Notifications.message(localized.ItemRestrictionDeleteConfirmation);
                            }
                            else
                                Notifications.message(localized.RestrictedItemUpdateSucessConfirmation);
                        }
                        else {
                            Notifications.error(localized.RestrictedItemFailUpdateNotification);
                        }
                        getRestrictedItems();
                    }
                });
            } else {
                Notifications.error(localized.EffectiveSaleDateRequired);
            }
        }
    }
    const submitItemRestriction = async (type) => {
        const addedItemList = addItemList;
        let request = [];
        addedItemList.map(selectedRow => {
            var items = {
                productId: selectedRow.id ?? null,
                itemNumber: selectedRow.sku ?? null,
                storefrontNumber: storeData.storefrontNumber,
                effectiveDateForSale: selectedRow.effectiveDateForSale ?? null,
                productDescription: selectedRow.description,
                catalogId: selectedRow.catalogId,
                operationType: type
            };
            request.push(items);
        })
        return await Storefront.updateRestrictedItems(request).then((res) => {
            if (res.response.endsWith("Success"))
                Notifications.message(localized.RestrictedItemAddConfirmation);
            else
                Notifications.error(localized.RestrictedItemFailUpdateNotification);

            getRestrictedItems();
        });
    }
    const handleUpdateDateChange = (e) => {
        const formatted = formatToYyyyDdMm(e);
        setSalesDate(formatted);
        if (e == null) {
            setDateError(true);
            Notifications.error(localized.EffectiveSaleDateRequired);
        }
        else {
            setDateError(false);
        }
    }
    const handleDateChange = (e) => {

        const formatted = formatToYyyyDdMm(e);
        setSalesDate(formatted);
        if (e == null) {
            setDateError(true);
            Notifications.error(localized.EffectiveSaleDateRequired);
        }
        else {
            setDateError(false);
        }

        if (!itemerror) {
            setAddFlage(false);
        }
    };
    const formatToYyyyDdMm = (date) => {
        return date ? format(date, dateFormat) : "";
    };

    const parseDate = (value) => {
        if (!value) return null;
        const parsed = parse(value, dateFormat, new Date());
        if (isValid(parsed)) return parsed;

        return null;
    };
    const renderAddModal = () => (
        <MUIModal
            open={openAdd}
            onClose={handleCloseAdd}
            title={localized.AddRestrictedItem}
            onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                const formJson = Object.fromEntries(formData.entries());
                updateItemRestriction(formJson, "add");
                handleCloseAdd();
            }}
            submitLabel={localized.StorefrontAdminAdd}
            submitButtonProps={{ color: "success" }}
            maxWidth={isVisible ? "md" : "sm"}
        >
            <DialogContentText>
                <h3>{localized.FillRequiredFieldsBelow}</h3>
            </DialogContentText>
            <Row>
                <Box
                    component="form"
                    sx={{ '& .MuiTextField-root': { m: 1, width: '30ch' } }}
                    noValidate
                    autoComplete="off"
                >
                    <Row>
                        <Col>
                            <div className="label">
                                {localized.FavoriteItem}*
                            </div>
                            <TextField
                                value={sku}
                                required
                                id="itemNumber"
                                name="itemNumber"
                                label={localized.ItemNumber}
                                type="text"
                                fullWidth
                                onBlur={handleBlur}
                                variant="standard"
                                error={itemerror}
                                onChange={handleChange}
                                inputProps={{
                                    maxLength: 50,
                                    onPaste: handlePaste,
                                    onKeyDown: handleKeyDown,
                                }}
                            />
                            {itemFetching &&
                                <Col>
                                    <CircularProgress size="30px" />
                                </Col>
                            }
                            <Typography
                                sx={{
                                    display: "-webkit-box",
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    WebkitLineClamp: 2,
                                    lineClamp: 2,
                                }}
                                style={{
                                    fontFamily: '"Gotham XNarrow SSm A", "Gotham XNarrow SSm B"',
                                    fontSize: "1.4rem",
                                }}
                            >
                                {description}
                            </Typography>
                            <div className="label" style={{ 'padding-top': '1rem' }}>
                                {localized.EffectiveSaleDate}*
                            </div>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                    value={salesDate ? parseDate(salesDate) : null}
                                    onChange={handleDateChange}
                                    disablePortal
                                    error={dateError}
                                    slotProps={{
                                        textField: {
                                            required: true,
                                            error: dateError,
                                            fullWidth: true,
                                            multiline: true,
                                            inputProps: { style: { fontSize: 14 } },
                                        },
                                        popper: {
                                            modifiers: [
                                                {
                                                    name: 'zIndex',
                                                    enabled: true,
                                                    phase: 'write',
                                                    fn: ({ state }) => {
                                                        state.styles.popper.zIndex = 1601;
                                                    },
                                                },
                                            ],
                                        },
                                    }}
                                />
                            </LocalizationProvider>
                        </Col>
                        <Col>

                            <CSSTransition
                                in={isVisible}
                                timeout={300}
                                classNames="sidebar-transition"
                                unmountOnExit
                            >
                                <div className={"border-start px-4 ms-4 border-black"}>
                                    <h4>   {localized.ItemRestrictionAdministrationHeader}</h4>
                                    <div className="claims-sidebar-content">
                                        {addItemList.map((item) => (
                                            <div className="d-flex justify-content-between text-start align-items-baseline ps-3">
                                                <div>
                                                    <p className="p-0 m-0">
                                                        <strong>{localized.Item}</strong>{" "}
                                                        <span style={{ fontSize: "2rem" }}>{item.sku}</span>
                                                        <IconButton
                                                            aria-label={localized.RemoveItem}
                                                            onClick={() => handleRemoveTempItem(item.sku)}
                                                        >
                                                            <DeleteOutline sx={{ fontSize: "21px", color: "black" }} />
                                                        </IconButton>
                                                    </p>

                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </CSSTransition>

                        </Col>
                    </Row>

                </Box>
            </Row>
            <DialogActions>
                <Button variant="standard" color="error" onClick={handleCloseAdd}>{localized.StorefrontAdminCancel}</Button>
                <Button variant="standard" color="success" onClick={handleAddItems} disabled={addFlage}>{localized.StorefrontAdminAdd}</Button>
                <Button variant="standard" color="success" type="submit" disabled={submitFlag}>{localized.Submit}</Button>
            </DialogActions>
        </MUIModal>
    );
    const renderDeleteButton = (row) => (
        <MUIAlertModal
            open={openDelete}
            onClose={handleCloseDelete}
            title={localized.AreYouSure}
            message={
                <>
                    <h3>{localized.AreYouSureDeleteRestrictedItem}</h3>
                    <DialogContentText>
                        <b>{row.code}</b>
                    </DialogContentText>
                </>
            }
            confirmLabel={localized.StorefrontAdminDelete}
            cancelLabel={localized.StorefrontAdminCancel}
            onConfirm={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                formData.append("Id", row.id);
                formData.append("Sku", row.sku);
                const formJson = Object.fromEntries(formData.entries());
                updateItemRestriction(formJson, "delete");
                handleCloseDelete();
            }}
            confirmButtonProps={{ color: "error" }}
            cancelButtonProps={{ color: "update" }}
        />
    );
    const renderUpdateButton = (row) => (
        <MUIModal
            open={openUpdate}
            onClose={handleCloseUpdate}
            title={localized.ItemRestrictionUpdateHeader}
            onSubmit={(event) => {
                event.preventDefault();
                const formData = new FormData(event.currentTarget);
                formData.append("Id", row.id);
                formData.append("Sku", row.sku);
                formData.append("EffectiveDateForSale", salesDate);
                const formJson = Object.fromEntries(formData.entries());
                updateItemRestriction(formJson, "update");
                handleCloseUpdate();
            }}
            submitLabel={localized.StorefrontAdminUpdate}
            cancelLabel={localized.StorefrontAdminCancel}
            showDefaultButtons={true}
            submitButtonProps={{ color: "update" }}
            cancelButtonProps={{ color: "update" }}
            maxWidth="sm"
            fullWidth
        >

            <TextField
                disabled
                id="standard-disabled"
                label="Item Number"
                defaultValue={row.sku}
                variant="standard"
                fullWidth
                inputProps={{ maxLength: 50 }}
            />
            <TextField
                disabled
                id="standard-disabled"
                label="Description"
                defaultValue={row.description}
                variant="standard"
                fullWidth
                inputProps={{ maxLength: 50 }}
                sx={{
                    "& .MuiInput-input": {
                        paddingTop: "1rem",
                    }
                }}
            />
            <div className="label" style={{ 'padding-top': '1rem' }}>{localized.EffectiveSaleDate}*</div>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                    value={salesDate ? parseDate(salesDate) : null}
                    onChange={handleUpdateDateChange}
                    disablePortal
                    slotProps={{
                        textField: {
                            required: true,
                            error: dateError,
                            fullWidth: true,
                            multiline: true,
                            inputProps: { style: { fontSize: 14 } },
                        },
                        popper: {
                            modifiers: [
                                {
                                    name: 'zIndex',
                                    enabled: true,
                                    phase: 'write',
                                    fn: ({ state }) => {
                                        state.styles.popper.zIndex = 1601;
                                    },
                                },
                            ],
                        },
                    }}
                />
            </LocalizationProvider>
        </MUIModal>
    );
    const handleClickOpenAdd = () => {
        setOpenAdd(true);
    };
    const handleCloseAdd = () => {
        setSku("");
        setDescription("");
        setAddItemList([]);
        setOpenAdd(false);
        setSubmitFlag(true);
        setDateError(false);
        setItemerror(false);
        setIsVisible(false);
        setItemFetching(false);
        setSalesDate(null);
        setAddFlage(true);
    };
    const handleAddItems = () => {
        if (sku != "" && salesDate && itemValid) {
            if (!addItemList.some(item => item.sku === sku) && (rows?.filter(x => x.sku == sku).length == 0)) {
                const addedItems = { sku: sku, description: description, effectiveDateForSale: salesDate, id: itemList.id, catalogId: itemList.catalogId };
                setAddItemList([...addItemList, addedItems]);
            }
            else {
                Notifications.error(localized.RestrictedItemExist);
            }
            setSalesDate(null);
            setSku("");
            setDescription("");
            setSubmitFlag(false);
            setAddFlage(true);
            setIsVisible(true);
        } else {
            if (!salesDate && sku != "") {
                setDateError(true);
                Notifications.error(localized.EffectiveSaleDateRequired);
            }
            else if (sku == "" && salesDate) {
                setItemerror(true);
                Notifications.error(localized.ItemRestrictionRequired);
            }
            else {
                setItemerror(true);
                setDateError(true);
                Notifications.error(localized.RestrictedItemValidation);
            }
        }

    };
    const handleRemoveTempItem = (removeItem) => {
        setAddItemList(prev => prev.filter(addItemList => addItemList.sku !== removeItem));
        if (addItemList.filter(x => x.sku !== removeItem).length == 0) {
            setSubmitFlag(true);
            setIsVisible(false);
        }
    }
    const restrictedItems = useMemo(() => {
        return (
            <Paper sx={{ height: 700, width: '100%' }}>
                <TranslatableDataGrid rows={rows} columns={columns} insetToolbar={customToolbar} labels={localized} />
            </Paper>
        );
    }, [rows, localized]);
    return (
        <>
            {
                fetching && <Loading type="brand" />
            }
            {
                !fetching && (localized && Object.keys(localized).length > 0) &&
                <>
                    <ThemeProvider theme={theme}>
                        <Typography variant="h1">{localized.ItemRestrictionAdministrationHeader}</Typography>
                        {restrictedItems}
                        {openUpdate && renderUpdateButton(selectedRow)}
                        {openDelete && renderDeleteButton(selectedRow)}
                        {openAdd && renderAddModal()}
                    </ThemeProvider>
                </>
            }
        </>
    );
};
export default ItemRestrictionAdministration